/**
 * HousingContextualGuide.js
 * Inline contextual guidance blocks for housing project pages.
 * 2-3 lines, directly tied to the page context. Not generic training.
 */
import React, { useState } from "react";
import { ChevronDown, ChevronRight, Lightbulb } from "lucide-react";

const GUIDES = {
  // Input Data page guides — per section
  "input_project_profile": [
    "When entering the units count, use your planning approval number if available — this is what a verifier will cross-reference.",
  ],
  "input_baseline": [
    "Common mistake: using block count instead of total weight. Cement quantities must be in kilograms, not units or bags.",
    "A conservative baseline (higher quantities) gives you more reduction credit. Do not understate your baseline inputs.",
  ],
  "input_project_scenario": [
    "SCM substitution % must be backed by a supplier certificate. A stated 40% without documentation is not acceptable at verification.",
    "If you are unsure of your solar fraction, use 25% — below your estimated 30%. Conservative assumptions protect you at verification.",
  ],
  "input_energy": [
    "If you do not have utility bills yet, use the national default: 4,200 kWh/yr for a 3BR unit in Nigeria (IEA 2023). This will need to be verified before submission.",
  ],

  // Evidence Vault guides — per category
  "evidence_materials": [
    "What a verifier will look for: Certificate number, supplier name, batch date, SCM percentage by weight. A delivery note alone is not sufficient.",
    "Acceptable formats: PDF supplier certificate, EPD (Environmental Product Declaration), or signed letter from supplier on company letterhead.",
  ],
  "evidence_energy": [
    "NERC-certified smart meters are the strongest evidence. If not installed, utility bills for the most recent 12 months are the accepted alternative.",
    "Solar generation data from the inverter monitoring system must show daily kWh readings. Manufacturer spec sheets alone are not evidence of actual generation.",
  ],
  "evidence_financial": [
    "To demonstrate additionality, you need both: (1) the actual procurement invoice for low-carbon materials, AND (2) a conventional construction cost quote showing the baseline. Without the comparison, there is no financial barrier proof.",
  ],

  // Carbon Strategy guides — per pathway
  "pathway_insetting": [
    "Insetting is the fastest and most accessible pathway. It does not require third-party verification or a carbon registry. Your buyer claims the reduction in their Scope 3 reporting.",
    "What you need: a documented supply agreement or MOU showing the buyer is claiming your emission reduction. This is the minimum required.",
  ],
  "pathway_vcm": [
    "The 3 things a VCM verifier will test immediately: (1) additionality — would this have happened anyway? (2) baseline — is your reference scenario defensible? (3) permanence — can the reduction be reversed?",
    "Your SCM cement substitution is durable (the building does not change after construction). That addresses permanence for embodied carbon.",
  ],
  "pathway_poa": [
    "PoA requires standardized design across all participating units. Any unit that deviates significantly from the demo unit design will need its own calculation — this increases cost per unit.",
  ],
};

function GuideItem({ text }) {
  return (
    <div className="flex items-start gap-2 text-[10px] text-teal-700">
      <span className="text-teal-400 flex-shrink-0 mt-0.5">→</span>
      <span>{text}</span>
    </div>
  );
}

export function ContextualGuide({ guideKey, title, collapsed = true }) {
  const [open, setOpen] = useState(!collapsed);
  const tips = GUIDES[guideKey];
  if (!tips?.length) return null;

  return (
    <div className="bg-teal-50 border border-teal-200 rounded-xl overflow-hidden"
      data-testid={`guide-${guideKey}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-teal-100 transition-colors">
        <Lightbulb size={13} className="text-teal-500 flex-shrink-0" />
        <p className="text-[10px] text-teal-700 font-semibold flex-1">{title || "Guidance"}</p>
        {open ? <ChevronDown size={12} className="text-teal-400" /> : <ChevronRight size={12} className="text-teal-400" />}
      </button>
      {open && (
        <div className="px-4 pb-3 space-y-1.5 border-t border-teal-200">
          {tips.map((tip, i) => <GuideItem key={i} text={tip} />)}
        </div>
      )}
    </div>
  );
}
